import React, { useEffect, useState } from 'react';
import { useT } from '../../../../helpers/i18n';
import { useDispatch, useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBed as falBed,
  faUsers as falUsers,
  faWaterLadder as falWaterLadder,
  faBath as falBath,
  faArrowsToDottedLine as falArrowsToDottedLine,
  faArrowsFromDottedLine as falArrowsFromDottedLine,
  faTrowelBricks as falTrowelBricks,
  faUserHelmetSafety as falUserHelmetSafety
} from '@fortawesome/pro-light-svg-icons';

import Checkers from '../../../../helpers/class/Checkers';
import { fetchPropertyBedrooms } from '../../../../redux/actions';
import BedroomsModal, { displayableBedrooms } from './BedroomsModal';

const STAT_ICONS = {
  bedrooms: falBed,
  sleeps: falUsers,
  pools: falWaterLadder,
  baths: falBath,
  areaSqm: falArrowsToDottedLine,
  areaOutdoorSqm: falArrowsFromDottedLine,
  constructionYear: falUserHelmetSafety,
  renovationYear: falTrowelBricks,
};

export default function OverviewSection ( props ) {
  const t = useT();

  const details = props.details || {};
  const isHotel = details.system_type_id == 2;

  const dispatch = useDispatch();
  const pageId = useSelector( state => state['propertiesParams']['page_id'] );
  const reduxPropertyId = useSelector( state => state['propertiesParams']['property_id'] );
  const propertyId = details.property_id || reduxPropertyId;
  const bedroomsData = useSelector( state => state['singlePropertyData']['modalBedrooms']['data'] );

  useEffect( () => {
    if ( propertyId != null ) {
      dispatch<any>( fetchPropertyBedrooms( pageId, propertyId ) );
    }
  }, [ pageId, propertyId ] );

  const modalBedrooms = displayableBedrooms( bedroomsData );

  const hasBedroomsModal = !isHotel && modalBedrooms.length > 0;

  const [ showBedroomsModal, setShowBedroomsModal ] = useState( false );

  const themes = Checkers.isValidArray( props.themes ) ? props.themes
      : Checkers.isValidArray( props.content?.propertyThemes ) ? props.content.propertyThemes
          : [];

  const toCount = ( v ) => {
    if ( v == null ) return 0;
    if ( typeof v === 'number' ) return v;
    const s = String( v ).trim();
    if ( s.length === 0 ) return 0;
    const m = s.match( /\d+/ );
    return m != null ? parseInt( m[0], 10 ) : 1;
  };

  const overview = props.details != null ? {
    bedrooms: hasBedroomsModal ? modalBedrooms.length : toCount( details.bedrooms_num ),
    capacityMax: toCount( details.capacity_max ),
    pools: toCount( details.pools ),
    baths: toCount( details.bathrooms_num ),
    wc: toCount( details.wc ),
    areaSqm: toCount( details.property_area_sqm ),
    areaOutdoorSqm: toCount( details.property_area_outdoor_sqm ),
    constructionYear: toCount( details.construction_year ),
    renovationYear: toCount( details.renovation_year ),
  } : ( props.content?.propertyOverview || {} );

  const stats = [];
  if ( overview.bedrooms > 0 && !isHotel ) {
    stats.push( { key: 'bedrooms', value: overview.bedrooms, label: t('property.bedrooms') } );
  }
  if ( overview.capacityMax > 0 ) {
    stats.push( { key: 'sleeps', value: overview.capacityMax, label: t('overview.sleeps') } );
  }
  if ( overview.pools > 0 ) {
    stats.push( { key: 'pools', value: overview.pools, label: t('overview.pools') } );
  }
  if ( overview.baths > 0 || overview.wc > 0 ) {
    const value = [ overview.baths > 0 ? overview.baths : null, overview.wc > 0 ? overview.wc : null ]
        .filter( ( v ) => v != null ).join( '/' );
    const label = [ overview.baths > 0 ? t('overview.baths') : null, overview.wc > 0 ? t('overview.wc') : null ]
        .filter( ( v ) => v != null ).join( '/' );
    stats.push( { key: 'baths', value: value, label: label } );
  }
  if ( overview.areaSqm > 0 ) {
    stats.push( { key: 'areaSqm', value: <span className="fs-24">{overview.areaSqm}m<sup>2</sup></span>, label: t('overview.indoorArea') } );
  }
  if ( overview.areaOutdoorSqm > 0 ) {
    stats.push( { key: 'areaOutdoorSqm', value: <span className="fs-24">{overview.areaOutdoorSqm}m<sup>2</sup></span>, label: t('overview.outdoorArea') } );
  }
  if ( overview.constructionYear > 0 ) {
    stats.push( { key: 'constructionYear', value: overview.constructionYear, label: t('overview.constructionYear') } );
  }
  if ( overview.renovationYear > 0 ) {
    stats.push( { key: 'renovationYear', value: overview.renovationYear, label: t('overview.renovationYear') } );
  }

  if ( themes.length === 0 && ( isHotel || stats.length === 0 ) ) {
    return null;
  }

  const widthClass = stats.length === 1 ? 12
      : stats.length === 2 ? 6
          : stats.length === 3 ? 4
              : stats.length === 4 ? 3
                  : ( stats.length === 5 || stats.length === 6 ) ? 2
                      : 1;

  return (
      <section id="stats" className="p-b-30 p-t-20 sm-p-b-0">
        <div id="overview"/>
        <div className="container">
          <div className="col-12 col-md-8 mx-auto">
            {
              themes.length > 0 &&
                <div className="text-center">
                  <p className="block-title m-t-40 hint-text m-b-0">{t('overview.attributes')}</p>
                  <h2 className="light m-t-5 m-b-0">
                    {themes.map( ( t ) => t.theme_title ).filter( Checkers.isValidString ).join( ', ' )}
                  </h2>
                </div>
            }
            {
              !isHotel && stats.length > 0 &&
                <div className="row m-t-30 justify-content-center">
                  {
                    stats.map( ( stat ) => {
                      const clickable = stat.key === 'bedrooms' && hasBedroomsModal;
                      const icon = STAT_ICONS[stat.key];
                      const inner = (
                          <>
                            <h3 className="no-margin">
                              <span>{stat.value}</span>
                              {
                                icon != null &&
                                  <>
                                    <br/>
                                    <FontAwesomeIcon icon={icon} className="fs-24 hint-text m-t-5"/>
                                  </>
                              }
                            </h3>
                            <p className="font-arial hint-text fs-12">{stat.label}</p>
                          </>
                      );
                      return (
                          <div className={'col-6 col-sm-' + widthClass + ' text-center'} key={stat.key}>
                            {
                              clickable ?
                                  <a href="javascript:void(0)" style={{ color: '#626262' }} onClick={() => setShowBedroomsModal( true )} aria-label={t('overview.bedroomsDetails')}>
                                    {inner}
                                  </a>
                                  :
                                  inner
                            }
                          </div>
                      );
                    } )
                  }
                </div>
            }
          </div>
        </div>
        {
          hasBedroomsModal &&
            <BedroomsModal
                bedrooms={bedroomsData}
                pageId={pageId}
                propertyId={propertyId}
                show={showBedroomsModal}
                onClose={() => setShowBedroomsModal( false )}
            />
        }
      </section>
  );
}
